import React from 'react';
import classNames from 'classnames';

import SVG from 'src/components/SvgIcon';
import { ReadingFeedbackFormData } from 'types/objectTypes';

import FollowUpSelect from './FollowUpSelect';
import styles from './ReadingFeedbackForm.module.scss';

const MEMORY_CLUE_MAX = 150;
const PEOPLE_DISCUSSED_MAX = 150;
const READING_COMMENTS_MAX = 1500;

interface FollowUpOption {
  value: string;
  label: string;
}

interface Props {
  formData: ReadingFeedbackFormData;
  readingTopics: string[];
  followUpOptions: FollowUpOption[];
  firstName?: string;
  onDataChange: (payload: Partial<ReadingFeedbackFormData>) => void;
  onRatingChange: (rating: 'thumbs_up' | 'thumbs_down') => void;
  onTopicToggle: (topic: string) => void;
}

const NewFeedbackTab: React.FC<Props> = ({
  formData,
  readingTopics,
  followUpOptions,
  firstName,
  onDataChange,
  onRatingChange,
  onTopicToggle,
}) => {
  const renderThumbButton = (rating: 'thumbs_up' | 'thumbs_down', svgId: string) => (
    <button
      type="button"
      className={styles.thumbBtn}
      onClick={() => onRatingChange(rating)}
      aria-label={rating === 'thumbs_up' ? 'Thumbs up' : 'Thumbs down'}
    >
      <SVG id={formData.rating === rating ? `${svgId}Selected` : svgId} />
    </button>
  );

  return (
    <>
      <div className={classNames(styles.sectionA, {
        [styles.bottomSpace]: !formData.selectedTopics.includes('Other'),
      })}
      >
        <div className={styles.row}>
          <span className={styles.rowLabel}>
            Did you have a psychic
            <br />
            {`connection with ${firstName}?`}
          </span>
          <div className={styles.thumbsGroup}>
            {renderThumbButton('thumbs_up', 'thumbsUp')}
            {renderThumbButton('thumbs_down', 'thumbsDown')}
          </div>
        </div>
        <div className={styles.followUP}>
          <FollowUpSelect
            label="Follow-up?"
            value={formData.followUpTimeline}
            options={followUpOptions}
            onChange={(val) => onDataChange({ followUpTimeline: val })}
          />
        </div>
        <div>
          <div className={styles.topicsLabel}>Reading topic(s)</div>
          <div className={styles.chipsGrid}>
            {readingTopics.map((topic) => (
              <button
                key={topic}
                type="button"
                className={`${styles.chip} ${formData.selectedTopics.includes(topic) ? styles.chipSelected : ''}`}
                onClick={() => onTopicToggle(topic)}
              >
                {topic}
              </button>
            ))}
          </div>
          {formData.selectedTopics.includes('Other') && (
            <p className={styles.otherHint}>Add Other specifics in Reading Details</p>
          )}
        </div>
      </div>

      <div className={styles.sectionB}>
        <div className={styles.sectionTitle}>Reading Details</div>

        <div className={styles.fieldGroup}>
          <label htmlFor="rf-memory-clue" className={styles.fieldLabel}>
            Memory Clue
            <div className={styles.inputWrapper}>
              <input
                id="rf-memory-clue"
                className={styles.input}
                type="text"
                placeholder="Enter a memory clue"
                maxLength={MEMORY_CLUE_MAX}
                value={formData.memoryClue}
                onChange={(e) => onDataChange({
                  memoryClue: e.target.value.trimStart().slice(0, MEMORY_CLUE_MAX),
                })}
              />
              {formData.memoryClue.length > 0 && (
                <span className={styles.inputCharCount}>
                  {`${formData.memoryClue.length}/${MEMORY_CLUE_MAX}`}
                </span>
              )}
            </div>
          </label>
        </div>

        <div className={styles.fieldGroup}>
          <label htmlFor="rf-people-discussed" className={styles.fieldLabel}>
            People discussed
            <div className={styles.inputWrapper}>
              <input
                id="rf-people-discussed"
                className={styles.input}
                type="text"
                placeholder="Enter names of people discussed"
                maxLength={PEOPLE_DISCUSSED_MAX}
                value={formData.peopleDiscussed}
                onChange={(e) => onDataChange({
                  peopleDiscussed: e.target.value.trimStart()
                    .slice(0, PEOPLE_DISCUSSED_MAX),
                })}
              />
              {formData.peopleDiscussed.length > 0 && (
                <span className={styles.inputCharCount}>
                  {`${formData.peopleDiscussed.length}/${PEOPLE_DISCUSSED_MAX}`}
                </span>
              )}
            </div>
          </label>
        </div>

        <div className={styles.fieldGroup}>
          <label htmlFor="rf-reading-comments" className={styles.fieldLabel}>
            Reading comments
            <div className={styles.textareaWrapper}>
              <textarea
                id="rf-reading-comments"
                className={styles.textarea}
                placeholder="Reading comments"
                maxLength={READING_COMMENTS_MAX}
                value={formData.readingComments}
                onChange={(e) => onDataChange({
                  readingComments: e.target.value.trimStart()
                    .slice(0, READING_COMMENTS_MAX),
                })}
              />
              {formData.readingComments.length > 0 && (
                <span className={styles.charCount}>
                  {`${formData.readingComments.length}/${READING_COMMENTS_MAX}`}
                </span>
              )}
            </div>
          </label>
        </div>
      </div>
    </>
  );
};

NewFeedbackTab.defaultProps = {
  firstName: '',
};

export default NewFeedbackTab;
